import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolve a stored image URL — backend upload paths start with /api/files/…
// and need the backend base prepended so the browser can fetch them.
export function resolveImage(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

const TOKEN_KEY = "ww_admin_token";

export const auth = {
  get token() {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  set(token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch {}
  },
  clear() {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
  },
  header() {
    const t = this.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  },
};

export async function login(email, password) {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  auth.set(data.access_token);
  return data.user;
}

export async function logout() {
  try { await axios.post(`${API}/auth/logout`); } catch {}
  auth.clear();
}

export async function fetchMe() {
  const { data } = await axios.get(`${API}/auth/me`, { headers: auth.header() });
  return data;
}

export async function adminListPaintings() {
  const { data } = await axios.get(`${API}/paintings`);
  return data;
}

export async function adminCreatePainting(body) {
  const { data } = await axios.post(`${API}/admin/paintings`, body, { headers: auth.header() });
  return data;
}

export async function adminUpdatePainting(id, body) {
  const { data } = await axios.put(`${API}/admin/paintings/${id}`, body, { headers: auth.header() });
  return data;
}

export async function adminDeletePainting(id) {
  const { data } = await axios.delete(`${API}/admin/paintings/${id}`, { headers: auth.header() });
  return data;
}

export async function adminDeleteAllPaintings() {
  const { data } = await axios.delete(`${API}/admin/paintings`, { headers: auth.header() });
  return data;
}

export async function adminReorderPaintings(ids) {
  const { data } = await axios.post(`${API}/admin/paintings/reorder`, { ids }, { headers: auth.header() });
  return data;
}

export async function changePassword(new_password, confirm_password) {
  const { data } = await axios.post(`${API}/auth/change-password`, { new_password, confirm_password }, { headers: auth.header() });
  return data;
}

export async function subscribe(email) {
  const { data } = await axios.post(`${API}/subscribe`, { email });
  return data;
}

export async function adminListSubscribers() {
  const { data } = await axios.get(`${API}/admin/subscribers`, { headers: auth.header() });
  return data;
}

export async function adminDeleteSubscriber(id) {
  const { data } = await axios.delete(`${API}/admin/subscribers/${id}`, { headers: auth.header() });
  return data;
}

export async function adminDownloadSubscribersCsv() {
  const { data } = await axios.get(`${API}/admin/subscribers.csv`, { headers: auth.header(), responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "subscribers.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function sendInquiry(body) {
  const { data } = await axios.post(`${API}/inquiries`, body);
  return data;
}

export async function adminListInquiries() {
  const { data } = await axios.get(`${API}/admin/inquiries`, { headers: auth.header() });
  return data;
}

export async function adminDeleteInquiry(id) {
  const { data } = await axios.delete(`${API}/admin/inquiries/${id}`, { headers: auth.header() });
  return data;
}

export function shareUrl(slug) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/s/${slug}`;
}

export function postShareUrl(slug) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/s/blog/${slug}`;
}

export async function listPosts(tag) {
  const { data } = await axios.get(`${API}/posts`, { params: tag ? { tag } : {} });
  return data;
}

export async function listPostTags() {
  const { data } = await axios.get(`${API}/posts/tags`);
  return data;
}

export async function fetchPost(slug) {
  const { data } = await axios.get(`${API}/posts/by-slug/${slug}`);
  return data;
}

export async function adminListPosts() {
  const { data } = await axios.get(`${API}/admin/posts`, { headers: auth.header() });
  return data;
}

export async function adminCreatePost(body) {
  const { data } = await axios.post(`${API}/admin/posts`, body, { headers: auth.header() });
  return data;
}

export async function adminUpdatePost(id, body) {
  const { data } = await axios.put(`${API}/admin/posts/${id}`, body, { headers: auth.header() });
  return data;
}

export async function adminDeletePost(id) {
  const { data } = await axios.delete(`${API}/admin/posts/${id}`, { headers: auth.header() });
  return data;
}

export async function listTestimonials() {
  const { data } = await axios.get(`${API}/testimonials`);
  return data;
}

export async function adminCreateTestimonial(body) {
  const { data } = await axios.post(`${API}/admin/testimonials`, body, { headers: auth.header() });
  return data;
}

export async function adminUpdateTestimonial(id, body) {
  const { data } = await axios.put(`${API}/admin/testimonials/${id}`, body, { headers: auth.header() });
  return data;
}

export async function adminDeleteTestimonial(id) {
  const { data } = await axios.delete(`${API}/admin/testimonials/${id}`, { headers: auth.header() });
  return data;
}

export async function adminUpload(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, form, {
    headers: { ...auth.header(), "Content-Type": "multipart/form-data" },
  });
  return data; // { url, path }
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
